import React, { ReactNode, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { HttpStatusCode } from 'axios'
import { Button, Page, Spinner } from '../../components'
import { Application } from '../../config'
import { restartIframeBooking } from '../../helpers'
import { appsStyleSelected } from '../../helpers/app'
import { IPaymentStatus, IPendingRedsysPayment } from '../../models'
import { urls } from '../../routing'
import { LiteralsService, ReservationService, StorageService } from '../../services'
import { checkSessionAction, confirmNewReservationSuccessfulAction, setNotificationAction } from '../../store/actions'
import StyledArticle from '../NewReservation/CompletedStep/styles'

const POLLING_INTERVAL_MILLISECONDS = 2000
const MAXIMUM_WAIT_MILLISECONDS = 60000

type PaymentResultState = 'checking' | 'pending' | 'failed' | 'notFound'

const { isIframe } = Application

const PaymentResult: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [pendingPayment] = useState<IPendingRedsysPayment | null>(() => StorageService.getPendingRedsysPayment())
  const [state, setState] = useState<PaymentResultState>(pendingPayment ? 'checking' : 'notFound')

  const goToReservation = (reservationId: number) => {
    StorageService.removePendingRedsysPayment()
    navigate(`${urls.reservationDetails}/${reservationId}`, { replace: true, state: { id: reservationId } })
  }

  const finishPayment = (reservationId: number, paymentStatus?: IPaymentStatus) => {
    if (paymentStatus === IPaymentStatus.SUCCEEDED || paymentStatus === IPaymentStatus.FAILED) {
      const succeeded = paymentStatus === IPaymentStatus.SUCCEEDED
      dispatch(setNotificationAction({
        isError: !succeeded,
        label: LiteralsService.get(succeeded ? 'bookPay' : 'payFailed', true)
      }))
    }
    goToReservation(reservationId)
  }

  // The iframe books for another user, whose reservation it cannot read back: the bank's answer is all it gets.
  const finishIframePayment = (payment: IPendingRedsysPayment) => {
    StorageService.removePendingRedsysPayment()
    if (searchParams.get('status') !== 'ok') {
      setState('failed')
      return
    }
    if (payment.purchaseCompleteEvent) {
      window.parent.postMessage(payment.purchaseCompleteEvent.message, payment.purchaseCompleteEvent.targetOrigin)
    }
    dispatch(confirmNewReservationSuccessfulAction(payment.userCreated))
    navigate(urls.newReservationCompletedStep, { replace: true })
  }

  useEffect(() => {
    if (!pendingPayment) return
    if (isIframe) {
      finishIframePayment(pendingPayment)
      return
    }

    dispatch(checkSessionAction())

    let isCancelled = false
    let pollingTimeout: ReturnType<typeof setTimeout> | undefined
    const deadline = Date.now() + MAXIMUM_WAIT_MILLISECONDS

    const pollPaymentStatus = async () => {
      try {
        const { paymentStatus } = await ReservationService.getReservation(pendingPayment.reservationId)
        if (isCancelled) return
        if (paymentStatus !== IPaymentStatus.CREATED) {
          finishPayment(pendingPayment.reservationId, paymentStatus)
          return
        }
      } catch (error: any) {
        if (isCancelled) return
        if (error?.status === HttpStatusCode.NotFound) {
          StorageService.removePendingRedsysPayment()
          dispatch(setNotificationAction({ isError: true, label: LiteralsService.get('payFailed', true) }))
          navigate(urls.reservations, { replace: true })
          return
        }
      }
      if (Date.now() >= deadline) {
        setState('pending')
        return
      }
      pollingTimeout = setTimeout(pollPaymentStatus, POLLING_INTERVAL_MILLISECONDS)
    }

    pollPaymentStatus()

    return () => {
      isCancelled = true
      clearTimeout(pollingTimeout)
    }
  }, [])

  const content = (): ReactNode => {
    switch (state) {
      case 'checking':
        return (
          <>
            <div className="subtitle">{ LiteralsService.get('paymentResult.checking', true) }</div>
            <Spinner />
          </>
        )
      case 'pending':
        return (
          <>
            <div className="subtitle">{ LiteralsService.get('paymentResult.pending', true) }</div>
            <Button
              title={ LiteralsService.get('paymentResult.seeBooking', true) }
              onPress={ () => pendingPayment && goToReservation(pendingPayment.reservationId) }
              filled
              upper
            />
          </>
        )
      case 'failed':
        return (
          <>
            <div className="subtitle">{ LiteralsService.get('payFailed', true) }</div>
            <Button title={ LiteralsService.get('startBooking', true) } onPress={ restartIframeBooking } filled upper />
          </>
        )
      default:
        return (
          <>
            <div className="subtitle">{ LiteralsService.get('paymentResult.notFound', true) }</div>
            <Button
              title={ LiteralsService.get(isIframe ? 'startBooking' : 'seeMyReservations', true) }
              onPress={ isIframe ? restartIframeBooking : () => navigate(urls.reservations, { replace: true }) }
              filled
              upper
            />
          </>
        )
    }
  }

  return (
    <Page className={ `authenticated ${appsStyleSelected?.style ?? ''}` }>
      <StyledArticle>
        <div className="text-container">
          { content() }
        </div>
      </StyledArticle>
    </Page>
  )
}

export default PaymentResult
